'use client'

import * as React from 'react'
import * as SwitchPrimitives from '@radix-ui/react-switch'
import { cva } from 'class-variance-authority'

import { cn } from '../../../lib/utils'

export interface SwitchProps {
  isChecked?: boolean
  bgSize?: 'small' | 'default'
  toggleSize?: 'small' | 'default'
  // Back-compat: prefer onCheckedChange on the component instance
  handleChange?: (value: boolean) => void
}

const bgVariants = cva(
  'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent p-0.5 shadow-sm transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-accent',
  {
    variants: {
      bgSize: {
        small: 'h-4 w-7',
        default: 'h-5 w-9',
      },
    },
    defaultVariants: {
      bgSize: 'default',
    },
  },
)

const toggleVariants = cva(
  'pointer-events-none block rounded-full bg-background shadow-lg ring-0 transition-transform duration-200 ease-in-out will-change-transform data-[state=unchecked]:translate-x-0',
  {
    variants: {
      toggleSize: {
        small: 'h-3 w-3 data-[state=checked]:translate-x-3',
        default: 'h-4 w-4 data-[state=checked]:translate-x-4',
      },
    },
    defaultVariants: {
      toggleSize: 'default',
    },
  },
)

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps & React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>
>(
  (
    {
      handleChange,
      className,
      isChecked,
      checked,
      onCheckedChange,
      defaultChecked,
      bgSize,
      toggleSize,
      ...props
    },
    ref,
  ) => {
    const checkedProp = isChecked ?? checked
    const isControlled = checkedProp !== undefined
    const [internalChecked, setInternalChecked] = React.useState(
      () => checkedProp ?? defaultChecked ?? false,
    )
    const skipPropSyncRef = React.useRef(false)

    React.useEffect(() => {
      if (!isControlled) return
      if (skipPropSyncRef.current) {
        skipPropSyncRef.current = false
        return
      }
      setInternalChecked(checkedProp)
    }, [checkedProp, isControlled])

    return (
      <SwitchPrimitives.Root
        className={cn(bgVariants({ bgSize }), className)}
        {...props}
        {...(isControlled ? { checked: internalChecked } : {})}
        {...(!isControlled && defaultChecked !== undefined
          ? { defaultChecked }
          : {})}
        ref={ref}
        onCheckedChange={(nextChecked) => {
          skipPropSyncRef.current = true
          if (isControlled) {
            setInternalChecked(nextChecked)
          }
          handleChange?.(nextChecked)
          onCheckedChange?.(nextChecked)
        }}
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        <SwitchPrimitives.Thumb
          className={cn(toggleVariants({ toggleSize }))}
        />
      </SwitchPrimitives.Root>
    )
  },
)
Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch }
